import React, { useCallback, useState } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet } from 'react-native';
import { Main } from '@expo/html-elements';
import { theme, useResponsive } from '@sg/garnish';

import {
  CheckoutLayout,
  CheckoutLayoutColumn,
  PageWrapper,
  SweetpassPaymentMethodAddModal,
} from '@order/components';
import { type DurationUnit, type SubscriptionCreditCard } from '@order/graphql';
import type { SweetpassMembershipPlans } from '@order/hooks';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import {
  SweetpassCheckoutBlock,
  SweetpassCheckoutCardBlock,
  SweetpassCheckoutHeader,
} from './components';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassCheckoutScreenContent = (
  props: SweetpassCheckoutScreenContentProps,
) => {
  const {
    testID,
    title,
    termsAndConditions,
    ctaLabel,
    planPrice,
    hasFreeTrial,
    creditCardsData,
    membershipPlansData,
    ctaHandler,
    billingFrequencyUnit,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { track } = useTelemetry();
  const { match, minWidth } = useResponsive();

  // ─── Telemetry ───────────────────────────────────────────────────────

  useTrackEffect('sweetpass.checkout.view', {
    isFreeTrial: hasFreeTrial,
    isMonthlySubscription: true,
    isAnnualSubscription: false,
  });

  // ─── State ───────────────────────────────────────────────────────────

  const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const hasCreditCards = (creditCardsData.creditCards?.length ?? 0) > 0;
  const canCheckout = hasAcceptedTerms && hasCreditCards;

  // ─── Callbacks ───────────────────────────────────────────────────────

  const handleCta = useCallback(async () => {
    setIsLoading(true);
    await ctaHandler();
    setIsLoading(false);
  }, [ctaHandler]);

  const handleAddCreditCard = useCallback(() => {
    track('sweetpass_account.add_payment_cta.tapped');
    setIsAddingPaymentMethod(true);
  }, [track]);

  const closeAddingPaymentMethodModal = useCallback(() => {
    setIsAddingPaymentMethod(false);
  }, []);

  const handleAcceptTerms = useCallback(
    (toggle: boolean) => {
      setHasAcceptedTerms(toggle);

      if (toggle) track('sweetpass_account.terms.viewed');
    },
    [track],
  );

  // ─── Styling ─────────────────────────────────────────────────────────

  const responsiveStyle = match([
    styles.wrapperXS,
    styles.wrapperSM,
    styles.wrapperMD,
  ]);
  const style = [styles.wrapper, responsiveStyle];

  const containerResponsiveStyle = match([
    styles.containerXS,
    styles.containerSM,
    styles.containerMD,
  ]);
  const containerStyles = [styles.container, containerResponsiveStyle];

  // NOTE: We render the payment method block in a different location
  //       (inside `SweetpassCheckoutBlock`) when the membership selector is enabled.
  const shouldRenderPaymentMethodsBlock =
    !membershipPlansData || (membershipPlansData && minWidth.isSM);

  return (
    <PageWrapper
      withHeaderTitle
      withoutFooter
      contentContainerStyle={style}
      testID="sg-sweetpass-checkout-wrapper"
    >
      <Main testID={testID} style={containerStyles}>
        <SweetpassCheckoutHeader>{title}</SweetpassCheckoutHeader>

        <CheckoutLayout>
          {shouldRenderPaymentMethodsBlock ? (
            <CheckoutLayoutColumn>
              <SweetpassCheckoutCardBlock
                selectedCreditCard={creditCardsData.selectedCreditCard}
                creditCards={creditCardsData.creditCards}
                handleSelectCreditCard={
                  creditCardsData.onSelectedCreditCardChange
                }
                handleAddCreditCard={handleAddCreditCard}
              />
            </CheckoutLayoutColumn>
          ) : null}

          <CheckoutLayoutColumn shouldFitToFill>
            <SweetpassCheckoutBlock
              billingFrequencyUnit={billingFrequencyUnit}
              termsAndConditions={termsAndConditions}
              cta={ctaLabel}
              planPrice={planPrice}
              hasFreeTrial={hasFreeTrial}
              hasAcceptedTerms={hasAcceptedTerms}
              canCheckout={canCheckout}
              isLoading={isLoading}
              membershipPlansData={membershipPlansData}
              handleCta={handleCta}
              handleAcceptTerms={handleAcceptTerms}
              creditCardsData={creditCardsData}
              handleAddCreditCard={handleAddCreditCard}
            />
          </CheckoutLayoutColumn>
        </CheckoutLayout>

        <SweetpassPaymentMethodAddModal
          visible={isAddingPaymentMethod}
          onRequestClose={closeAddingPaymentMethodModal}
        />
      </Main>
    </PageWrapper>
  );
};

// ─── Types ─────────────────────────────────────────────────────────────

type SweetpassCheckoutScreenContentProps = Readonly<{
  title: string;
  termsAndConditions: string;
  ctaLabel: string;
  planPrice: number;
  hasFreeTrial: boolean;
  ctaHandler: (() => Promise<void>) | (() => void);
  creditCardsData: {
    creditCards: ReadonlyArray<Partial<SubscriptionCreditCard>>;
    selectedCreditCard?: Partial<SubscriptionCreditCard>;
    onSelectedCreditCardChange: (
      creditCard: Partial<SubscriptionCreditCard>,
    ) => void;
  };
  membershipPlansData?: {
    plans: SweetpassMembershipPlans;
    selectedPlanId?: string;
    onSelectedPlanIdChange: (planId: string) => void;
    freeTrialNote?: string;
  };
  billingFrequencyUnit?: DurationUnit;
}> &
  Pick<ViewProps, 'testID'>;

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flexGrow: 1,
    backgroundColor: theme.colors.GREEN_2,
  },
  wrapperXS: {
    backgroundColor: theme.colors.OATMEAL,
  },
  wrapperSM: {
    paddingTop: theme.spacing['6'],
  },
  wrapperMD: {
    paddingTop: theme.spacing['10'],
  },
  container: {
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['6'],
  },
  containerXS: {
    flexGrow: 1,
  },
  containerSM: {
    maxWidth: 500,
  },
  containerMD: {
    maxWidth: 1024,
  },
});
